import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import { faComments, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';

interface SendTestDiscordMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTestMessageSent: () => void;
}

export default function SendTestDiscordMessageModal({ isOpen, onClose, onTestMessageSent }: SendTestDiscordMessageModalProps) {
  const [formData, setFormData] = useState({
    channelId: '',
    content: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.channelId.trim()) {
      showErrorToast('Bitte geben Sie eine Channel-ID ein');
      return;
    }

    if (!formData.content.trim()) {
      showErrorToast('Bitte geben Sie einen Nachrichteninhalt ein');
      return;
    }


    setLoading(true);

    try {
      await APIRoutes.sendDiscordMessage({
        channelId: formData.channelId.trim(),
        content: formData.content.trim(),
      });
      showSuccessToast('Test-Discord-Nachricht wurde erfolgreich versendet');
      onTestMessageSent();
      onClose();

      // Reset form
      setFormData({
        channelId: '',
        content: '',
      });
    } catch (err) {
      handleAPIError(err, 'Fehler beim Senden der Test-Discord-Nachricht');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = formData.channelId.trim() && formData.content.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Test-Discord-Nachricht senden"
      icon={<FontAwesomeIcon icon={faComments} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="channelId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Discord Channel-ID *
            </label>
            <input
              type="text"
              id="channelId"
              name="channelId"
              value={formData.channelId}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              placeholder="123456789012345678"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nachrichteninhalt *
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={4}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-vertical"
              placeholder="Test-Nachricht von BolzAI"
              required
              disabled={loading}
            />
          </div>


          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Die Discord-Nachricht wird in die Warteschlange eingereiht und automatisch versendet.
              Sie können den Status der Nachricht in der Liste verfolgen.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faComments} className="w-4 h-4" />
                <span>Discord-Nachricht senden</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}